// Feature A — AI Placemat draft panel.
//
// Rendered inside ArtifactDetailPage for placemat artifacts. Shows the governed
// 8-section AI draft with full run chrome (run id, agent, confidence, evidence
// count, guardrail, generated-at), the grounding chip, the citations list, and a
// governed "Approve placemat" action (typed rationale required via
// ConfirmActionDialog). For live projects it also offers on-demand
// draft / re-draft. AI law: nothing is approved without the human rationale gate.
import { useState } from 'react';
import { GovernedRunChrome } from '../../components/inspectors/GovernedRunChrome.jsx';
import { AiGroundingChip } from '../../components/inspectors/AiGroundingChip.jsx';
import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { EmptyState, StatusChip, toast } from '../../components/index.js';
import { useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import {
  ProvenanceBadge,
  normalizedProvenance,
} from '../../components/ProvenanceBadge.jsx';

const SECTION_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
  marginTop: 12,
};

export function PlacematDraftPanel({
  artifactId,
  artifact,
  placematDraft,
  projectLive = false,
  onChange,
  onApproved,
}) {
  const draftMut = useMutation();
  const approveMut = useMutation();
  const [approveOpen, setApproveOpen] = useState(false);

  const hasDraft = Boolean(placematDraft?.runId);
  const runProvenance = hasDraft ? normalizedProvenance(placematDraft, 'ai') : null;
  const artifactProvenance = normalizedProvenance(artifact, 'artifact');
  const approved =
    placematDraft?.humanReviewState === 'human_reviewed' ||
    placematDraft?.reviewState === 'human_reviewed' ||
    placematDraft?.reviewState === 'approved';
  const sections = Array.isArray(placematDraft?.sections) ? placematDraft.sections : [];
  const anySectionBody = sections.some((section) => section.body);

  async function runDraft() {
    try {
      const response = await draftMut.run(() =>
        api.post(`/artifacts/${artifactId}/placemat-draft`, {}),
      );
      onChange?.({ placematDraft: response.data.run });
      toast.success({
        title: 'AI placemat drafted',
        body: 'A governed draft is ready for human review.',
      });
    } catch (error) {
      toast.error({ title: 'Placemat draft failed', body: error.message });
    }
  }

  async function approve(rationale) {
    try {
      const response = await approveMut.run(() =>
        api.post(`/artifacts/${artifactId}/placemat-approve`, {
          runId: placematDraft.runId,
          rationale,
        }),
      );
      setApproveOpen(false);
      onChange?.({ placematDraft: response.data.run, artifact: response.data.artifact });
      // IMP-005: approval also mints records metadata + audit events that the
      // sibling side panels (Record posture, Audit summary) render from a
      // separate fetch. Signal the parent to refetch so those panels live-update
      // instead of staying stale until a manual reload.
      onApproved?.();
      toast.success({
        title: 'Placemat approved',
        body: 'The placemat is now an approved official record.',
      });
    } catch (error) {
      toast.error({ title: 'Placemat approval blocked', body: error.message });
    }
  }

  return (
    <section className="knowledge-panel placemat-draft-panel" data-testid="placemat-draft-panel">
      <div className="row spread" style={{ gap: 12 }}>
        <div>
          <div className="eyebrow">AI placemat draft</div>
          <h4 style={{ margin: '4px 0 0' }}>Governed placemat one-pager</h4>
        </div>
        {hasDraft ? (
          <StatusChip status={approved ? 'approved' : placematDraft.reviewState || 'pending_human_review'} />
        ) : null}
      </div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <ProvenanceBadge value={artifact} kind="artifact" />
        {artifactProvenance.sourceMode === 'generated_output' ? (
          <span className="knowledge-provenance-note">Generated output</span>
        ) : null}
        {hasDraft ? <ProvenanceBadge value={placematDraft} kind="ai" /> : null}
      </div>

      {!hasDraft ? (
        <div className="knowledge-stack">
          <EmptyState
            title="No AI placemat draft yet"
            description="Draft a governed placemat one-pager grounded on the approved intake and the generated scaffold."
          />
          {projectLive ? (
            <button
              type="button"
              className="btn primary sm"
              onClick={runDraft}
              disabled={draftMut.running}
            >
              {draftMut.running ? 'Drafting…' : 'Draft placemat with AI'}
            </button>
          ) : (
            <p className="muted small">Placemat drafting is closed for archived projects.</p>
          )}
        </div>
      ) : (
        <>
          {placematDraft.groundedIn ? <AiGroundingChip groundedIn={placematDraft.groundedIn} /> : null}
          {runProvenance?.confidence.value != null ? (
            <p className="muted small">
              {runProvenance.confidence.source === 'model'
                ? 'Model confidence'
                : runProvenance.confidence.source === 'rules'
                  ? 'Rules confidence'
                  : runProvenance.confidence.source === 'heuristic'
                    ? 'Heuristic confidence'
                    : runProvenance.confidence.source === 'authored'
                      ? 'Authored confidence'
                      : 'Confidence · source unknown'}{' '}
              · {Math.round(runProvenance.confidence.value * 100)}%
            </p>
          ) : null}
          <GovernedRunChrome result={{ ...placematDraft, confidence: null }} />

          <div style={SECTION_GRID_STYLE}>
            {sections.map((section) => (
              <article
                key={section.id}
                className="knowledge-row"
                data-testid={`placemat-section-${section.id}`}
              >
                <div className="eyebrow">{section.title}</div>
                <p className="muted">{section.body || 'To be confirmed during scoping.'}</p>
              </article>
            ))}
          </div>

          {placematDraft.narrative && !anySectionBody ? (
            <p className="muted" data-testid="placemat-narrative">
              {placematDraft.narrative}
            </p>
          ) : null}

          <div className="placemat-citations" style={{ marginTop: 12 }}>
            <div className="eyebrow">Evidence</div>
            {(placematDraft.citations || []).length === 0 ? (
              <p className="muted small">No citations recorded.</p>
            ) : (
              <ul className="knowledge-stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {placematDraft.citations.map((citation, index) => (
                  <li
                    key={citation.citationId || citation.source || index}
                    className="knowledge-row"
                  >
                    <div className="row spread" style={{ gap: 12 }}>
                      <strong>{citation.source || citation.citationId}</strong>
                      {typeof citation.score === 'number' ? (
                        <span className="chip outline sm">
                          {runProvenance?.citationScoreSource === 'keyword_heuristic'
                            ? 'keyword match strength'
                            : runProvenance?.citationScoreSource === 'provider_retrieval'
                              ? 'provider retrieval score'
                              : runProvenance?.citationScoreSource === 'authored'
                                ? 'authored source score'
                                : 'source score'}{' '}
                          {citation.score.toFixed(2)}
                        </span>
                      ) : null}
                    </div>
                    {citation.excerpt ? <p className="muted small">{citation.excerpt}</p> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {approved ? (
              <span className="chip blue sm">Official record</span>
            ) : (
              <button
                type="button"
                className="btn primary sm"
                onClick={() => setApproveOpen(true)}
                disabled={approveMut.running}
              >
                Approve placemat
              </button>
            )}
            {projectLive ? (
              <button
                type="button"
                className="btn outline sm"
                onClick={runDraft}
                disabled={draftMut.running}
              >
                {draftMut.running ? 'Drafting…' : 'Re-draft with AI'}
              </button>
            ) : null}
          </div>
        </>
      )}

      <ConfirmActionDialog
        open={approveOpen}
        title="Approve placemat"
        body="Approving marks this AI placemat draft human-reviewed, flips the artifact to an approved official record, and writes an auditable decision with retention metadata."
        confirmLabel="Approve placemat"
        cancelLabel="Cancel"
        tone="primary"
        requireNote
        noteLabel="Approval rationale"
        busy={approveMut.running}
        onConfirm={approve}
        onCancel={() => setApproveOpen(false)}
      />
    </section>
  );
}
